/**
 * Holds the notification tags an app entry arrived with, so the landing screen can report
 * them even though the URL that carried them is gone by then.
 *
 * `cid` and `ch` ride on every notification link the backend sends. They are readable at
 * entry — the browser's location on a page load, the launch URL on native — and only
 * there. Expo Router rebuilds the address bar from its own navigation state as soon as it
 * resolves a route, so a parameter no route declares survives only as long as the state it
 * was parsed into; a guard that renders nothing while it decides collapses that state and
 * takes the parameter with it. That is why the tags reach `/inbox`, whose route is the one
 * the collapse lands on, and never reach `/inbox/chat/[id]`.
 *
 * Reading them at entry sidesteps the question entirely, and closes the native case at the
 * same time: a `primum://` link carries both tags and never touches an address bar at all.
 *
 * One-shot by design. The tags name the send that caused this entry, so exactly one screen
 * — the one the reader landed on — should carry them. Left standing they would attribute
 * every later screen in a session, which on native outlives the notification by days.
 */

import type { CommunicationAttribution } from '@/util/deepLinkAnalytics';

let pendingAttribution: CommunicationAttribution | null = null;

/**
 * Records the tags an entry arrived with.
 *
 * An entry carrying neither is ignored rather than stored, so an ordinary navigation
 * cannot displace a notification still waiting for the screen it landed on.
 */
export function recordCommunicationAttribution(attribution: CommunicationAttribution): void {
  if (!attribution.communicationId && !attribution.communicationChannel) {
    return;
  }

  pendingAttribution = attribution;
}

export function takeCommunicationAttributionProperties(): Record<string, string> {
  const attribution = pendingAttribution;

  pendingAttribution = null;

  return communicationAttributionProperties(attribution);
}

/**
 * Shared with the events that carry an attribution they parsed themselves, so the property
 * names — which are a warehouse contract — live in one place.
 */
export function communicationAttributionProperties(
  attribution: CommunicationAttribution | null | undefined
): Record<string, string> {
  if (!attribution) {
    return {};
  }

  return {
    ...(attribution.communicationId ? { communication_id: attribution.communicationId } : {}),
    ...(attribution.communicationChannel
      ? { communication_channel: attribution.communicationChannel }
      : {}),
  };
}

/** Test seam. Clears the marker so one test's entry cannot attribute another's screen. */
export function clearCommunicationAttribution(): void {
  pendingAttribution = null;
}
